import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { XMarkIcon, PencilIcon, CloudArrowUpIcon, XCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link as LinkIcon } from 'lucide-react';

const sanitizeAndFormatHtml = (content) => {
    if (!content || typeof content !== 'string') return '';
    let html = content;
    html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
        const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
        if (!attrMatch) return tag;
        const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
        const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
        const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
        return `<a href="${rawUrl}"${targetAttr}>`;
    });
    if (!/<(?:br|p|div)\b/i.test(html)) {
        html = html.replace(/\r?\n/g, '<br />');
    }
    return html;
};

const OverviewModal = ({ overview, loanType, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        notice_text: '',
        loan_type: loanType || 'home_construction_loan',
        features: [],
        is_active: true
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [featureInput, setFeatureInput] = useState('');
    const [loading, setLoading] = useState(false);

    // Link inserter state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const handleInsertLink = () => {
        if (!linkUrl.trim()) {
            toast.error('Please enter a valid URL (e.g. https://ahamhfc.com/home-loan-blog-aham-housing-finance/40)');
            return;
        }
        const label = linkText.trim() || 'NRI home loan eligibility in India';
        const cleanUrl = linkUrl.trim();
        const tag = `<a href="${cleanUrl}"><strong><u>${label}</u></strong></a>`;
        const currentValue = formData.notice_text || '';
        const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
        setFormData(prev => ({ ...prev, notice_text: updatedValue }));
        toast.success(`Inserted link: ${label}`);
        setShowLinkModal(false);
        setLinkText('');
        setLinkUrl('');
    };

    // Initialize form data when editing
    useEffect(() => {
        if (overview) {
            setFormData({
                title: overview.title || '',
                description: overview.description || '',
                notice_text: overview.notice_text || '',
                loan_type: overview.loan_type || loanType || 'home_construction_loan',
                features: typeof overview.features === 'string'
                    ? JSON.parse(overview.features)
                    : overview.features || [],
                is_active: overview.is_active !== undefined ? overview.is_active : true
            });

            if (overview.image_url) {
                setImagePreview(`http://localhost:8001${overview.image_url}`);
            }
        }
    }, [overview, loanType]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle image selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageFile(file);
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Add feature
    const addFeature = () => {
        if (featureInput.trim()) {
            setFormData(prev => ({
                ...prev,
                features: [...prev.features, featureInput.trim()]
            }));
            setFeatureInput('');
        }
    };

    // Remove feature
    const removeFeature = (index) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Description is required');
            return;
        }

        setLoading(true);

        try {
            const submitData = new FormData();
            submitData.append('loan_type', formData.loan_type);
            submitData.append('title', formData.title);
            submitData.append('description', formData.description);
            submitData.append('notice_text', formData.notice_text || '');
            submitData.append('features', JSON.stringify(formData.features));
            submitData.append('is_active', formData.is_active ? '1' : '0');

            if (imageFile) {
                submitData.append('image', imageFile);
            }

            if (overview) {
                // Update existing overview
                await axios.put(`/api/admin/loan-overview/${overview.id}`, submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Overview updated successfully');
            } else {
                // Create new overview
                await axios.post('/api/admin/loan-overview', submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Overview created successfully');
            }

            onSave();
        } catch (error) {
            console.error('Error saving overview:', error);
            toast.error(error.response?.data?.message || 'Failed to save overview');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

            {/* Modal */}
            <div className="relative min-h-screen flex items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-primary-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
                        <div className="flex items-center gap-3">
                            <PencilIcon className="h-6 w-6" />
                            <div>
                                <h2 className="text-xl font-bold">
                                    {overview ? 'Edit Overview' : 'Add New Overview'}
                                </h2>
                                <p className="text-primary-100 text-sm">Manage overview content</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-primary-700 rounded-lg transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder="e.g., Home Construction Loan"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                rows={5}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                                placeholder="Enter the overview description..."
                            />
                            <p className="text-xs text-slate-500 mt-2">Supports line breaks</p>
                        </div>

                        {/* Notice / Guide Paragraph with Link Inserter */}
                        <div className="border-t border-slate-100 pt-5 space-y-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700">
                                        Notice / Guide Paragraph (Between Description and Bullet Points)
                                    </label>
                                    <p className="text-xs text-slate-500">
                                        Appears immediately after the overview description and before the bullet points list.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowLinkModal(!showLinkModal);
                                        setLinkText('');
                                        setLinkUrl('');
                                    }}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-800 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors border border-primary-200"
                                >
                                    <LinkIcon className="w-3.5 h-3.5" />
                                    <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
                                </button>
                            </div>

                            {showLinkModal && (
                                <div className="bg-primary-50/80 border border-primary-200 rounded-xl p-3 mb-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-primary-900">Insert Link into Paragraph</span>
                                        <span className="text-[11px] text-primary-700">e.g. https://ahamhfc.com/home-loan-blog-aham-housing-finance/40</span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                                            <input
                                                type="text"
                                                value={linkText}
                                                onChange={e => setLinkText(e.target.value)}
                                                placeholder="e.g. NRI home loan eligibility in India"
                                                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                                            <input
                                                type="text"
                                                value={linkUrl}
                                                onChange={e => setLinkUrl(e.target.value)}
                                                placeholder="e.g. https://ahamhfc.com/home-loan-blog-aham-housing-finance/40"
                                                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => setShowLinkModal(false)}
                                            className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleInsertLink}
                                            className="px-3 py-1 text-xs font-bold bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-sm"
                                        >
                                            Insert &lt;a&gt; Tag
                                        </button>
                                    </div>
                                </div>
                            )}

                            <textarea
                                name="notice_text"
                                value={formData.notice_text || ''}
                                onChange={handleChange}
                                rows={3}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none text-sm"
                                placeholder='Eligibility for an NRI home loan can depend on factors such as income, employment, repayment capacity and documentation. Read our detailed guide to <a href="https://ahamhfc.com/home-loan-blog-aham-housing-finance/40"><strong><u>NRI home loan eligibility in India</u></strong></a> to understand who can apply.'
                            />
                            <p className="text-[11px] text-slate-500">
                                Tip: HTML anchor tags and formatting are fully supported. Use &ldquo;+ Insert Link&rdquo; to quickly format links.
                            </p>

                            {/* Live Preview */}
                            {formData.notice_text && (
                                <div className="mt-3 bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                            <span className="text-xs font-bold uppercase tracking-wider text-slate-700">PARAGRAPH LIVE PREVIEW</span>
                                        </div>
                                        <span className="text-[11px] text-slate-500">Live preview with clickable styled link</span>
                                    </div>
                                    <div
                                        className="text-slate-700 text-sm leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors"
                                        dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(formData.notice_text) }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Overview Image */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Overview Image <span className="text-slate-400">(Optional)</span>
                            </label>
                            <p className="text-xs text-slate-500 mb-3">
                                📐 Recommended: 800 x 600 pixels | Formats: WebP, PNG, JPEG
                            </p>

                            <div className="flex gap-4">
                                {/* Upload Area */}
                                <div className="flex-1">
                                    <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="hidden"
                                        />
                                        <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                        <p className="text-sm font-medium text-slate-600">
                                            {imagePreview ? 'Change Image' : 'Upload Image'}
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1">Max 10MB</p>
                                    </label>
                                </div>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Features */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Key Features
                            </label>
                            <div className="flex gap-2 mb-3">
                                <input
                                    type="text"
                                    value={featureInput}
                                    onChange={(e) => setFeatureInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                                    className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    placeholder="Add a feature and click Add"
                                />
                                <button
                                    type="button"
                                    onClick={addFeature}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                    Add
                                </button>
                            </div>

                            {/* Feature Tags */}
                            {formData.features.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {formData.features.map((feature, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-sm font-medium"
                                        >
                                            {feature}
                                            <button
                                                type="button"
                                                onClick={() => removeFeature(index)}
                                                className="hover:text-blue-800"
                                            >
                                                <XCircleIcon className="h-4 w-4" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Active Status */}
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-3 cursor-pointer bg-slate-50 px-4 py-3 rounded-xl hover:bg-slate-100 transition-colors">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                                />
                                <span className="text-sm font-semibold text-slate-700">Active (Show on website)</span>
                            </label>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4 border-t border-slate-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Saving...' : overview ? 'Update Overview' : 'Create Overview'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default OverviewModal;
